import type { APIError } from "$lib/def/api";
import type { User } from "proto/user";

import { browser } from "$app/environment";
import { PUBLIC_BUILD_NUMBER, PUBLIC_BUILD_VERSION, PUBLIC_BUILD_VERSION_SOURCE, PUBLIC_ENV } from "$env/static/public";
import { clear, getItem, observe, setItem } from "$lib/serv/core/local-store";
import { setTag, setUser } from "@sentry/sveltekit";
import { BehaviorSubject, EMPTY, Subject, catchError, combineLatestWith, delay, distinctUntilChanged, distinctUntilKeyChanged, filter, finalize, from, map, mergeWith, of, scan, shareReplay, startWith, switchMap, take, tap, throwError, withLatestFrom, type Observable } from "rxjs";
import { fromFetch as rxFetch } from "rxjs/fetch";
import { apiHost$, caApiHost$, caApiPaths$, cdnHost$, proxyEnabled } from "./conf";
import { setCookie as _setCookie } from "$lib/util/cookie";
import { extractErrorMessage } from "$lib/util/format";

export type LoginFlow = 'SIGN_IN_BASIC' | 'SIGN_UP' | 'OTP';

const KEY = 'auth';
const KEY_PROFILE = 'user';
const DEBUG = false;

export const CLIENT_BUILD_NR = (+(PUBLIC_BUILD_NUMBER || '99000') + 999).toString();

/**
 * Version naming convention:
 * - dev: v2.dev
 * - qa: v2.qa.[github action build number]
 * - pp: v2.#.#-rc# (git tag name)
 * - prod: v2.#.# (git tag name)
 */
const env = PUBLIC_ENV.toLowerCase();
const verDev = `v2.${env}`;
const verQA = `v2.qa.${PUBLIC_BUILD_NUMBER}`;
const useSource = ['prod','pp'].some(e => env === e); // which will be the tag name
export const CLIENT_VERSION = useSource ? PUBLIC_BUILD_VERSION_SOURCE : env === 'qa' ? verQA : verDev;

// a subject that bridges the login success to the profile observable
const bridgeLogin$ToProfile$ = new Subject<User>();
// a subject that allows the auth$ observable to be muted - avoiding side effects like double fetches
const observerTake = new BehaviorSubject(true);

let fromFetch = rxFetch;
let setCookie = _setCookie;

interface AuthDetail {
	token: string,
	userId: number,
}

const auth$ = browser ? observe<string | AuthDetail | null>(KEY).pipe(
	withLatestFrom(observerTake),
	// auth key could be null/undefined, a string, or AuthDetail object
	distinctUntilChanged(([{ value: prev}], [{ value: curr }]) => {
		// if both values are falsy, nothing has changed
		if (!prev && !curr) return true;
		// if only one of them is falsy, something has changed
		if (!prev || !curr) return false;

		// otherwise, we are comparing strings and/or AuthDetails
		return (typeof prev === "string" ? prev : prev.token)
			=== (typeof curr === "string" ? curr : curr.token);
	}),
	filter(([, take]) => take),
	map(([{ value }]) => value),
	filter(value => value !== 'Og=='), // `:`
) : EMPTY; // server-side auth is not supported

/**
 * Refresh for when you update profile info from dedicated page.
 */
const refresh$ = new Subject<void>();
export const refreshProfile = () => refresh$.next();

/**
 * Emits a `User` when the user is logged in and `null` when not.
 */
export const profile$ = auth$.pipe(
  switchMap(value => refresh$.pipe(
		startWith(0),
		// NB: after login, auth$ does not get nexted so we need to grab the auth token manually if that is the case
		map(() => value || getItem(KEY)),
	)),
	// for each auth key, fetch the user profile
	switchMap(value => {
		// for null auth, return null user
		if (!value) return of(null);
		return replaceHost(`api://v3/users`).pipe(
			tap(req => req.headers.set('authorization', `Basic ${getAuthToken(value)}`)),
			switchMap(req => getAnon$<User>(req)),
		);
	}),
	catchError(er => {
		// in case of an error, clear the auth key
		if (er?.status === 401) {
			console.warn('Invalid auth. Logging out.');
			setItem(KEY);
			return of(null);
		}
		// request failed, but you are likely a legit user
		const provProfile = getItem(KEY_PROFILE); // THIS MUST BE THE ONLY PLACE WHERE WE READ THE PROFILE FROM LOCAL STORAGE - NO EXCEPTIONS
		if (provProfile) {
			console.warn('Unknown auth response. Falling back to cached profile.');
			return of(provProfile as User);
		}

		// no previous profile to restore - we need at least a user id so we can't continue
		console.warn('Unknown auth response and no fall back profile. Logging out.');
		setItem(KEY);
		return of(null);
	}),
	// store the profile for non-401 errors
	tap(user => setItem(KEY_PROFILE, user)),
	// make a way for login success and register success to update the profile directly
	mergeWith(bridgeLogin$ToProfile$),
	// setup sentry user
	tap({ next: user => setUser(user && { ...user, id: user?.id?.toString(), email: user?.name } || null) }),
	// an attempt at setting additional environment data on the senty user
	// tap({ next: user => {
	// 	const ctx = user && { ...user, id: user?.id?.toString(), email: user?.name, storage: {} } || null
	// 	try {
	// 		if (ctx && navigator?.storage?.estimate) {
	// 			navigator.storage.estimate().then(est => {
	// 				ctx.storage = est;
	// 				setUser(ctx);
	// 			}).catch(() => setUser(ctx));
	// 		} else setUser(ctx);
	// 	} catch (error) {
	// 		setUser(ctx);
	// 	}
	// }}),
	shareReplay(1),
);

/**
 * 
 * @param email User email address.
 * @param password User password.
 * @returns A cold observable that will verify the user credentials.
 */
export function login(email: string, password: string, verificationChannel?: 'SMS' | 'EMAIL', verificationToken?: string) {
	const value = btoa(`${email}:${password}`);
	const apiVersion = getItem<string>('auth-api-version') ?? 'v4';
	return replaceHost(`api://${apiVersion}/authenticated-user`).pipe(
		tap(req => {
			req.headers.set('authorization', `Basic ${value}`);
			if (verificationToken) {
				req.headers.set('verification-token', verificationToken);
			} else if (verificationChannel) {
				req.headers.set('verification-channel', verificationChannel);
			}

		}),
		switchMap(req => get$<User>(req)),
		tap(user => {
			// save auth to local storage and prevent profile$ from reacting on the change
			observerTake.next(false);
			setItem(KEY, {
				token: value,
				userId: user.id,
			});
			setItem(KEY_PROFILE, user);
			observerTake.next(true);
		}),
		// Set cookie when auth changes (fire and forget)
		tap(user => setCookie('auth', 'true')),
		tap(user => user && bridgeLogin$ToProfile$.next(user)),
	);
}

export function getLoginFlow(email: string) {
	return postAnon$<{ login_flow: LoginFlow }>(`api://v3/users/login-flow`, { email })
}

/**
 * For a authenticated user to update there password. Not for an unauthenticated user's password reset.
 * @param password New password.
 * @returns User profile
 */
export function updatePassword(password: string) {
	const $ = profile$.pipe(
		switchMap(user => put$<User>(`api://v3/users/${user?.id}`, { password })), // what android does
	);
	return pipeSaveAndBridge(password, $);
}

/**
 * For a unauthenticated user to register an account. Not for an authenticated user.
 * @param email User email address.
 * @param password User password.
 * @param first_name User first name.
 * @param last_name User last name.
 * @param phone_1 User phone number.
 * @returns User profile details.
 */
export function register(email: string, password: string, first_name: string, last_name: string, phone_1: string) {
	const $ = post$<User>(`api://v3/users`, {
		id: 0, name: email, password, profile: { email, first_name, last_name, phone_1 }
	});
	return pipeSaveAndBridge(password, $);
}

function pipeSaveAndBridge(password:string, $:Observable<User>) {
	return $.pipe(
		tap(user => {
			// save auth to local storage and prevent profile$ from reacting on the change
			observerTake.next(false);
			setItem(KEY, {
				token: btoa(`${user.name}:${password}`),
				userId: user.id,
			});
			setItem(KEY_PROFILE, user);
			observerTake.next(true);
		}),
		tap(user => user && bridgeLogin$ToProfile$.next(user)),
	);
}

/**
 * Removes all user persisted data.
 */
export function logout() {
	clear();
	setCookie('auth', 'false');
}

// ############################################################
// AUTHENTICATE API REQUESTS
// ############################################################

type URLString = string;
type Path = string;

const HOST: {
	[id: string]: {
		value$: Observable<URLString>,

		/**
		 * Some pathnames may be rerouted to different hosts.
		 */
		paths$?: Observable<[Path, URLString][]>,

		/**
		 * If true, the host (porbably Mr D platform) expects additional headers
		 */
		expectHeaders?: boolean,

		/**
		 * If true the host is allowed to be proxied through the local `/prox` route.
		 * See `server.proxy` in vite.config.ts proxy options.
		 */
		allowProxy?: boolean,
	}
} = {
	'self': { value$: of(''), expectHeaders: true }, // auth header needed for push message topic subscription, see api/push/topic-sub
	'cdn': { value$: cdnHost$ },
	'api': { value$: apiHost$, expectHeaders: true, allowProxy: true },
	'ca': { value$: caApiHost$, expectHeaders: true, allowProxy: true, paths$: caApiPaths$ },
}

// let auth: string | AuthDetail | null;
// observe<string | AuthDetail>(KEY).subscribe(({ value }) => auth = value);

const fetchCountIncrement = new Subject<{ inc: number, id?: string }>();
export const fetchCount$ = fetchCountIncrement.pipe(
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	scan((acc, { inc, id }) => {
		if (DEBUG) console.info(inc > 0 ? '+>' : '<-', acc + inc, id);
		return acc + inc;
	}, 0),
	startWith(0),
	shareReplay(1),
);
fetchCount$.subscribe();
// TODO: Gareth, please confirm if we can remove this subscribe();
// Reply: No. Only while subscribed to will `acc` perist and count correctly.


// from GW: this should rather be done on each individual cart services
// Loader for the cart in the nav
export const fetchCartCount$ = fetchCountIncrement.pipe(
	filter(({ id }) => !!id?.includes('/cart/')),
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	scan((acc, { inc, id }) => {
		return acc + inc;
	}, 0),
	startWith(0),
	shareReplay(1),
);


/**
 * A helper function to JSON stringify the body and set the content-type header appropriately.
 * @param body Any JS object that will be stringified.
 * @returns A RequestInit object.
 */
const fetch = (method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', req:Request, body?: any) => {
	if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
		req.headers.set('Content-Type', 'application/json');
	}
	return fromFetch(req, { method, body: body ? JSON.stringify(body) : undefined });
}

/**
 * Performs a GET with basic auth header.
 * @param input URL `[host key]://path`.
 * @param includeUserId Indicate whether to include user id in the request if auth is available
 * @returns Fetch observable.
 */
export function get$<T>(input: string | Request, includeUserId = false): Observable<T> {
	return replaceHost(input).pipe(
		switchMap((req) => setHeaders(req, true, includeUserId)),
		// switchMap(req => handleFetch<T>(fromFetch(req).pipe(retry({count: 2, delay:700})), `GET ${req.url}`)),
		switchMap(req => handleFetch<T>(fetch('GET', req), `GET ${req.url}`)),
	)
}

/**
 * Performs a GET with no auth header.
 * @param input URL `[host key]://path`.
 * @returns Fetch observable.
 */
export function getAnon$<T>(input: string | Request): Observable<T> {
	return replaceHost(input).pipe(
		switchMap((req) => setHeaders(req, false)),
		switchMap(req => handleFetch<T>(fetch('GET', req), `GET ${req.url}`))
	)
}

/**
 * Performs a POST with basic auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @param includeUserId Indicate whether to include user id in the request if auth is available
 * @returns Fetch observable.
 */
export function post$<T>(url: string, body: any, includeUserId = false) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req, true, includeUserId)),
		switchMap(req => handleFetch<T>(fetch('POST', req, body), `POST ${req.url}`))
	);
}

/**
 * Performs a POST with no auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @returns Fetch observable.
 */
export function postAnon$<T>(url: string, body: any) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req, false)),
		switchMap(req => handleFetch<T>(fetch('POST', req, body), `POST ${req.url}`))
	);
}

/**
 * Performs a PUT with basic auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @returns Fetch observable.
 */
export function put$<T>(url: string, body: any) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req)),
		switchMap(req => handleFetch<T>(fetch('PUT', req, body), `PUT ${req.url}`)),
	)
}

/**
 * Performs a PUT with no auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @returns Fetch observable.
 */
export function putAnon$<T>(url: string, body: any) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req, false)),
		switchMap(req => handleFetch<T>(fetch('PUT', req, body), `PUT ${req.url}`)),
	)
}

/**
 * Performs a PATCH with basic auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @returns Fetch observable.
 */
export function patch$<T>(url: string, body: any) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req)),
		switchMap(req => handleFetch<T>(fetch('PATCH', req, body), `PATCH ${req.url}`)),
	);
}

/**
 * Performs a PATCH with basic auth header.
 * @param input URL `[host key]://path`.
 * @param body An object that will be stringified.
 * @returns Fetch observable.
 */
export function patchAnon$<T>(url: string, body: any) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req, false)),
		switchMap(req => handleFetch<T>(fetch('PATCH', req, body), `PATCH ${req.url}`)),
	);
}

/**
 * Performs a DELETE with basic auth header.
 * @param input URL `[host key]://path`.
 * @returns Fetch observable.
 */
export function delete$<T>(url: string) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req)),
		switchMap(req => handleFetch<T>(fetch('DELETE', req), `DELETE ${req.url}`))
	);
}

/**
 * Performs a DELETE with no auth header.
 * @param input URL `[host key]://path`.
 * @returns Fetch observable.
 */
export function deleteAnon$<T>(url: string) {
	return replaceHost(url).pipe(
		switchMap((req) => setHeaders(req, false)),
		switchMap(req => handleFetch<T>(fetch('DELETE', req), `DELETE ${req.url}`))
	);
}

function replaceHost(request: string | Request): Observable<Request> {
	const url = typeof request === 'string' ? request : request.url;
	let split = url.split('://');
	// ODD: Sometimes url arrives looking like "ca:grocery/product/abc123"
	if (split.length === 1) split = url.split(':');

	if (split.length > 1) {
		const host = split[0] as keyof typeof HOST;
		if (host in HOST) {
			if (host === 'self' && !browser) console.warn('"self://" is not available server-side.');
			return HOST[host].value$.pipe(
				take(1), // <- without this the request does not complete due to config stream not completing as intended
				combineLatestWith(proxyEnabled.value$.pipe(take(1)), HOST[host].paths$?.pipe(take(1)) ?? of([])),
				map(([h, prox, paths]) => {
					let req: Request;
					const removeLeadingSlash = (str: string) => str.startsWith('/') ? str.substring(1) : str;
					const cleanPaths = paths.map(([path, host]) => [removeLeadingSlash(path), host]);
					const pathnames = cleanPaths.sort((a, b) => b[0].length - a[0].length);
					const pathname = pathnames.find(([path]) => split[1].startsWith(path));
					if (pathname) { // re-routed path is a higher priority than proxying
						const [, host] = pathname;
						//console.log(`rerouting: ${split[1]} to ${host}/${split[1]}`);
						req = new Request(`${host || h}/${split[1]}`, request instanceof Request ? request : undefined);
					} else if (browser && HOST[host].allowProxy && prox) {
						req = new Request(`prox/${split[0]}/${split[1]}`, request instanceof Request ? request : undefined);
					} else {
						req = new Request(`${h}/${split[1]}`, request instanceof Request ? request : undefined);
					}
					if (HOST[host].expectHeaders) req.headers.set('req-headers', 'true');
					return req;
				}),
			);
		}
	} else if (!browser) {
		console.warn('An unspecified host URL is not allowed server-side.');
	}
	return of(typeof request === 'string' ? new Request(request) : request);
}

// ### Small helper functions to retrieve auth token or user id ###

/**
 * Retrieve the Basic auth token from local storage.  It could either be the Basic auth string,
 * or it could be inside an AuthDetail object.
 * @returns The Basic auth string
 */
function getAuthToken(stored?: AuthDetail | string): string | null {
	const auth = stored ?? getItem<AuthDetail | string>(KEY);
	if (typeof auth === "string") return auth;
	return auth?.token ?? null;
}

/**
 * Retrieve the user id from local storage, if available.
 * @returns An Observable that emits one value: either the logged-in user's id or null
 */
function getUserId():Observable<number | null> {
	const auth = getItem<AuthDetail | string>(KEY);
	if (typeof auth === "object" && 'userId' in auth) return of(auth.userId);

	return profile$.pipe(
		take(1),
		map((user) => user?.id ?? null),
	);
}
/**
 * Add the necessary headers to the request object.
 * @param req The HTTP request object.
 * @param authed Add Authorization header.
 * @param includeUserHeader Add x-user-id header.
 * @returns The same request instance, but with the necessary headers set.
 */
function setHeaders(req:Request, authed = true, includeUserHeader = false): Observable<Request> {
	if (!req.headers.has('req-headers')) return of(req);

	req.headers.delete('req-headers');
	req.headers.set('Accept', 'application/json'); // exclude text/plain
	req.headers.set('x-api-key', '8991c720-203f-4e18-af78-d4b32aa49005');
	req.headers.set('x-client-id', 'WEB_APP');
	req.headers.set('x-client-version', CLIENT_VERSION);
	req.headers.set('x-client-build-number', CLIENT_BUILD_NR);
	req.headers.set('x-platform-version', browser ? navigator.userAgent : 'Node v' + process.version);

	const auth = getAuthToken();
	if (authed && auth) {
		req.headers.set('authorization', `Basic ${auth}`);

		if (includeUserHeader) {
			return getUserId().pipe(
				map((userId) => {
					if (userId) req.headers.set('x-user-id', userId.toString());
					return req;
				}),
			);
		}
	}

	return of(req);
}
 // TODO: add unit tests for this function
function handleFetch<T>($:Observable<Response>, name?: string) {
	return of(null).pipe(
		tap(() => fetchCountIncrement.next({ inc: 1, id: name })),
		// switchMap(() => obsUntilOnline()), // prevent obvious issues when offline
		switchMap(() => $),
		switchMap(res => {
			if (('ok' in res && res.ok) || (res.status >= 200 && res.status < 300)) {
        return from(res.json() as Promise<T>).pipe(
					catchError((err) => of(null as T)), // if JSON parsing fails, return null - no need to make a fuss about it
				);
			}
			// get Sentry some context
			const traceId = res.headers?.get('x-trace-id') ?? res.headers?.get('trace-id');
			setTag('api-error-response-trace-id', traceId ?? 'unknown');

			// TODO: log the user out if the error is 401
			const error_code = 'UNEXPECTED_' + res.status;
			const friendly_message = 'Oof! That didn\'t work. Please try again later.';
			return from(res.json()).pipe(
				catchError(er => from(res.text()).pipe( // after a JSON error, try to extract the text
					map(text => ({
						error_code,
						friendly_message,
						developer_message: `Server error response: ${res.status} >>> ${text}`,
					})),
				)),
				catchError(er => of({
					error_code,
					friendly_message,
					developer_message:`Server responded with ${res.status} and no body.`,
				})), // lets clean up the text extraction error
				map(er => er.error ?? er), // if needed, flatten the error object
				map(er => Object.assign(new Error(extractErrorMessage(er, 'ERR '+(name??'API response'))), er) as APIError & Error),
				tap(errorTap), // escalate system-wide type errors
				switchMap(er => throwError(() => er)), // convert to error observable
			)
		}),
		finalize(() => fetchCountIncrement.next({ inc: -1, id: name })),
	);
}

// ############################################################
// GLOBAL API ERROR HANDLING
// ############################################################

const errorStream = new Subject<APIError>();

function errorTap(er?: APIError) {
	if (er?.error_code === 'APP_SHUT_OUT' || er?.error_code === 'API_DOWN') {
		errorStream.next(er);
	}
}

/**
 * Emits an API error when platform is in maintenance mode or if paltform reports that the app is out of date.
 */
export const apiError$ = errorStream.pipe(
	filter(({ error_code: id }) => id === 'API_DOWN' || id === 'APP_SHUT_OUT'),
	distinctUntilKeyChanged('error_code'), // prevent multiple parallel API requests all reporting the same error
)

// ############################################################
// UNIT TEST HELPERS
// ############################################################

export function setMock<T>(f?: typeof rxFetch<T>) {
	if (PUBLIC_ENV !== 'dev') throw new Error('This function is only available in dev mode.');
	fromFetch = f as typeof rxFetch ?? rxFetch;
}
export function createMockResponse(body?: any, status = 200) {
	return {
		ok: status >= 200 && status < 300,
		status,
		json: () => Promise.resolve(body),
	}
}
export function setMockHost(key: keyof typeof HOST, host: typeof HOST[keyof typeof HOST]) {
	HOST[key] = host;
}
export function setSetCookie(enabled = false) {
	setCookie = enabled ? _setCookie : () => Promise.resolve();
}

/**
 * A pathname for a mock fetch. May contain parameters like `:id`.
 * Eg. `/grocery/cart/:id`
 */
export type MockRoutePath = string;
export type MockBody = object;
export type MockResponderReturnImmediate = { $body?: MockBody, $status?: number } | MockBody;
export type MockResponderReturn = MockResponderReturnImmediate | Promise<MockResponderReturnImmediate>;
export type MockResponder = (url:string, body?:object, headers?: Headers ) => MockResponderReturn;

let instCount = 0;
export class Mocker {

	responders: [ string, string, MockResponder ][] = [];
	prefix = '';

	constructor(logPrefix = '') {
		this.prefix = `${logPrefix} ${instCount++}`;
	}

	/**
	 * Adds at mock responder for a specific path and method.
	 * @param path The pathname to match. May contain parameters eg. `/order/:id/details`.
	 * @param cb A callback that will be called when the path matches. The callback should
	 * return a response object or a promise that resolves to a response object.
	 * @returns The mocker instance handy for chaining.
	 */
	private action(path:MockRoutePath, cb:MockResponder){
		// THIS FUNCTION IS USED FOR DOCUMENTATION PURPOSES ONLY
		return this;
	}

	private push = (method:string) => (path:MockRoutePath, cb:MockResponder) => {
		this.responders.push([path, method, cb])
		return this;
	};

	/** {@inheritdoc Mocker.action} */
	get = this.push('GET');

	/** {@inheritdoc Mocker.action} */
	post = this.push('POST');
	
	/** {@inheritdoc Mocker.action} */
	put = this.push('PUT');
	
	/** {@inheritdoc Mocker.action} */
	patch = this.push('PATCH');
	
	/** {@inheritdoc Mocker.action} */
	delete = this.push('DELETE');

	/**
	 * Creates a mock `fetch` function.
	 * @param responseDelay The delay in milliseconds before the fetch responds.
	 * @param defaultStatus The default status code for the response. Can be overridden per
	 * responder.
	 * @param passthrough If `true`, requests that do not have a responder will be passed
	 * through to the real fetch.
	 * @param log A logger function that will emit the request details. Emits will
	 * (obviously) occur before the emit so add your own 0 delay if needed.
	 * @param logBodiesWithSpace The number of spaces to use for JSON.stringify when
	 * logging the request and response bodies. If `undefined`, no bodies will be logged.
	 * @returns A function that can be used as a `fetch` mock.
	 */
	createFetch(
		responseDelay = 30,
		defaultStatus = 200,
		passthrough = false,
		log?:{next:(name:string)=>void},
		logBodiesWithSpace:Parameters<typeof JSON.stringify>[2] = undefined,
	) {
		let n = 0;
		return (input:RequestInfo, init?:RequestInit) => {
			// const req = new Request(input, init);
			// const { url, method, headers } = req;
			// let body: Promise<any>;
			// try {
			// 	body = req.text().then(t => t?.length ? req.json() : {});
			// } catch (error) {
			// 	body = Promise.resolve({});
			// }
			const url = typeof input === 'string' ? input : input.url;
			let method = typeof input === 'string' ? 'GET' : input.method.toUpperCase();
			let body = Promise.resolve({} as any);
			let headers = typeof input === 'string' ? new Headers() : input.headers;
			if (typeof init !== 'undefined') {
				if (typeof init.method !== 'undefined') {
					method = init.method;
				}
				if (typeof init.body !== 'undefined') {
					const bodyStr = init.body?.toString();
					if (bodyStr) body = Promise.resolve(JSON.parse(bodyStr));
				}
				if (init.headers) {
					headers = new Headers(init.headers);
				}
			}
			const responder = this.responders.find(([path, meth]) => compareRoutePath(path, url) && meth === method);
			if (typeof responder === 'undefined' && passthrough) {
				return rxFetch(input, init).pipe(
					tap(() => log?.next(`${this.prefix}.${n++} ${method} [!] ${url}`)),
				);
			}
			const [,,cb] = responder ?? [];
			if (!cb) {
				return of(createMockResponse(undefined, defaultStatus)).pipe(
					delay(responseDelay),
					tap(() => log?.next(`${this.prefix}.${n++} ${method} [!] ${url}`)),
				);
			}
			return from(body).pipe(
				switchMap(b => {
					const res = cb(url, b, headers);
					const copyAndAugmentResponse = (r: typeof res) => ({
						response: Array.isArray(r) ? [...r] : {...r},
						$out: b,
					});
					if (res instanceof Promise) return res.then(copyAndAugmentResponse);
					return of(copyAndAugmentResponse(res));
				}),
				map(b => {
					const $payload = b.$out;
					delete b.$out;
					const rtn = !b.response
						? { $status: defaultStatus }
						: ('$body' in b.response || '$status' in b.response
							? b.response
							: { $body: b.response, $status: defaultStatus });
					return { ...rtn, $payload }
				}),
				map(({$body, $status, $payload}) => ({ res: createMockResponse($body, $status), $body, $payload })),
				delay(responseDelay),
				tap(({$body, $payload}) => {
					const bodies = typeof logBodiesWithSpace === 'undefined' ? '' : ` ${JSON.stringify($payload,null,logBodiesWithSpace)} >> ${JSON.stringify($body,null,logBodiesWithSpace)}`;
					log?.next(`${this.prefix}.${n++} ${method} ${url}${bodies}`);
				}),
				map(({ res }) => res),
			);
		}
	}
}

function compareRoutePath(path:MockRoutePath, fullUrl:string) {
	const pathParts = path.startsWith('/') ? path.substring(1).split('/') : path.split('/'); // remove leading slash
	const url = fullUrl.split('?')[0]; // remove query params
	const urlParts = ~url.indexOf('//') ? url.split('/').splice(3) : url.split('/'); // remove protocol and domain

	if (pathParts.length !== urlParts.length) return false;
	for (let i = 0; i < pathParts.length; i++) {
		if (pathParts[i] === urlParts[i]) continue;
		if (pathParts[i].startsWith(':')) continue;
		return false;
	}
	return true;
}

// TODO: handle down mode throughout the app. Error response:
// {
// 	"error": {
//			 "title": "Uh Oh!",
//			 "friendly_message": "We are currently experiencing technical difficulties and will be back shortly.",
//			 "developer_message": "API down",
//			 "error_code": "API_DOWN"
// 	}
// }
// AND
// {
// 	"error": {
// 		"http_response_code": 400,
// 		"developer_message": "Your app is out of date. To order, visit the app store & download the latest version.",
// 		"friendly_message": "Your app is out of date. To order, visit the app store & download the latest version.",
// 		"error_code": "APP_SHUT_OUT"
// 	}
// }
// PREFERED COPY: "A new version of this website is available. Update it to continue"
// CTA: Update now
